import type { SyntheticEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { contactsApi } from '../api/contacts'
import { RefusedError } from '../api/client'

// A rep's vote on a contact (REQ-123, owner 2026-09-28): "important" raises them to the top of every list where
// contacts are ranked; "less" keeps them a prospect but sinks them below everyone else. Per signed-in rep; a vote
// never hides a person. Two toggles: clicking the one that is on takes the vote back (DELETE), clicking the other
// sets it (PUT). Every write invalidates every screen that ranks or shows a contact, so the row moves at once.

/** Prefix keys: every query that carries a contact's vote or an ordering built on it. */
const INVALIDATES = ['contact-card', 'contact', 'contacts', 'account', 'outreach', 'workbench']

const WORDS = {
  up: { glyph: '▲', label: 'Important', title: 'Important: at the top of every list where contacts are ranked' },
  down: { glyph: '▼', label: 'Less', title: 'Less: still a prospect, shown below everyone else' },
} as const

function message(e: unknown): string {
  return e instanceof RefusedError ? e.refusal.message : e instanceof Error ? e.message : String(e)
}

export function VoteButtons({ spoke, personId, vote, compact = false, invalidate = [] }: {
  spoke: string
  personId: string
  /** the rep's current vote: 1 up, -1 down, null none */
  vote: number | null
  /** glyphs only, for a table cell or a list row */
  compact?: boolean
  /** extra query keys (prefixes) to invalidate after a write, beyond the standard set */
  invalidate?: string[][]
}) {
  const qc = useQueryClient()
  const settle = () => {
    for (const k of INVALIDATES) void qc.invalidateQueries({ queryKey: [k] })
    for (const key of invalidate) void qc.invalidateQueries({ queryKey: key })
  }
  // one mutation, so the last outcome is the one shown: null takes the vote back, 1 or -1 sets it
  const write = useMutation({
    mutationFn: async (v: 1 | -1 | null) => { if (v === null) await contactsApi.clearVote(spoke, personId); else await contactsApi.vote(spoke, personId, v) },
    onSuccess: settle,
  })
  const current = vote ?? 0
  const up = current > 0
  const down = current < 0
  const toggle = (v: 1 | -1) => write.mutate((v > 0 ? up : down) ? null : v)
  // works inside a clickable row: a vote is never also an "open"
  const stop = (e: SyntheticEvent) => e.stopPropagation()

  return (
    <span className={`vote-buttons${compact ? ' compact' : ''}`} onClick={stop} role="group" aria-label="Your vote on this contact">
      <button type="button" className={`button small up${up ? ' on' : ''}`} aria-pressed={up} aria-label={compact ? WORDS.up.label : undefined} title={WORDS.up.title} onClick={() => toggle(1)} disabled={write.isPending}>
        {WORDS.up.glyph}{compact ? '' : ` ${WORDS.up.label}`}
      </button>
      <button type="button" className={`button small down${down ? ' on' : ''}`} aria-pressed={down} aria-label={compact ? WORDS.down.label : undefined} title={WORDS.down.title} onClick={() => toggle(-1)} disabled={write.isPending}>
        {WORDS.down.glyph}{compact ? '' : ` ${WORDS.down.label}`}
      </button>
      {write.isError && <span className="error small">{message(write.error)}</span>}
    </span>
  )
}

/** The badge a vote leaves on a row: "important" or "less"; nothing when there is no vote. */
export function VoteBadge({ vote }: { vote: number | null | undefined }) {
  const v = vote ?? 0
  if (v > 0) return <span className="tag vote up" title={WORDS.up.title}>important</span>
  if (v < 0) return <span className="tag vote down" title={WORDS.down.title}>less</span>
  return null
}
